import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Dialog from 'src/componentes/dialog';
import StatusItemLookup from 'src/lookups/status-item';
import Input from 'src/componentes/input';
import useFormAdapter from 'src/utils/useFormAdapter';
import schema from './ModalFluxoAprovacaoSchema';
import useAuth from 'src/hooks/useAuth';
import Button from 'src/componentes/button';
import http from 'src/services/httpService';
import Alert from 'src/componentes/message-box/alert';
import ContentManager from 'src/componentes/content-manager';
import { Spinner } from 'reactstrap';
import errorHandler from 'src/utils/error-handler';
import hextToRgb from 'src/utils/hexToRgb';

export const ModalFluxoAprovacaoStatus = ({
  enviadoParaAprovacao,
  statusAtual,
  statusDestino,
  statusSolicitacao,
  fluxoAprovacao,
  riscoModel,
  parameters,
  observacaoSolicitacao,
  statusIds,
  ehReenvio = false,
  ...dialogProps
}) => {
  const intl = useIntl();
  const form = useFormAdapter(schema);
  const model = form.getModel();
  const { terms: resources } = useAuth();
  const dialogRef = useRef(null);
  const [enviandoAprovacao, setEnviandoAprovacao] = useState(false);

  useEffect(() => {
    form.setValue('statusSolicitado', statusDestino);
  }, []);

  const close = () => {
    dialogRef.current?.close();
  };

  const openConfirmAlert = () => {
    ContentManager.addContent(
      <Alert
        message={intl.formatMessage({
          id: 'labelSolicitacaoAlteracaoStatusEnviadaAprovacaoComSucesso',
        })}
        type="success"
      />
    );
  };

  const enviarStatusParaAprovacao = async () => {
    if (!form.isValid()) {
      form.showAllErrors();
      ContentManager.addContent(
        <Alert
          message={intl.formatMessage({
            id: 'label.osCamposDestacadosEmVermelhoSaoObrigatoriosFavorPreenchelos',
          })}
        />
      );
      return;
    }

    try {
      setEnviandoAprovacao(true);

      await http.post('Risco/EnviarStatusParaAprovacao', {
        model: riscoModel,
        parameters,
        statusDestino: model.getModel('statusSolicitado').value,
        justificativa: model.getModel('justificativa').value,
      });

      setEnviandoAprovacao(false);
      openConfirmAlert();
      close();
      enviadoParaAprovacao(true);
    } catch (error) {
      setEnviandoAprovacao(false);
      errorHandler(error);
    }
  };

  const actions = [
    <Button key={1} onClick={close} className="mr-2" color="secondary">
      {intl.formatMessage({ id: 'label.cancelar' })}
    </Button>,
    <Button
      key={2}
      disabled={enviandoAprovacao}
      onClick={() => enviarStatusParaAprovacao()}
    >
      <div className="d-flex align-items-center">
        <div className={enviandoAprovacao ? 'pr-1' : ''}>
          {ehReenvio
            ? intl.formatMessage({ id: 'labelReenviarAprovacao' })
            : intl.formatMessage({ id: 'enviarParaAprovacao' })}
        </div>
        {enviandoAprovacao && <Spinner color="light" size="sm" />}
      </div>
    </Button>,
  ];

  const renderStatus = () => {
    const rgba = hextToRgb(statusSolicitacao?.cor, 10);
    return (
      <div
        style={{
          background: rgba,
          color: statusSolicitacao?.cor,
          textAlign: 'center',
        }}
        className="d-inline-flex py-1 px-2 shadow-sm mr-2 text-sm font-weight-bold rounded font-weight-bolder"
      >
        {intl.formatMessage({ id: statusSolicitacao?.descricao })}
      </div>
    );
  };

  const renderJustificativa = (justificativa) => {
    return (
      <div
        style={{
          width: '100%',
          minWidth: 0,
          maxHeight: '130px',
          overflow: 'auto',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
      >
        {justificativa}
      </div>
    );
  };

  return (
    <Dialog
      ref={dialogRef}
      minWidth={500}
      maxWidth={500}
      title={intl.formatMessage(
        { id: 'labelSolicitacaoAlteracaoStatusRisco' },
        {
          risco: resources.risco,
        }
      )}
      ignoreResourceTitle
      textCapitalizeTitle={false}
      titleCapitalizeNone
      padContent
      actions={actions}
      {...dialogProps}
    >
      <div className="d-flex flex-column gap-2" style={{ minWidth: 0 }}>
        <div className="d-flex gap-1">
          {intl.formatMessage({ id: 'labelStatusAtual' })}:
          <span style={{ fontWeight: 'bold' }}>{statusAtual?.descricao}</span>
        </div>
        <div>
          <StatusItemLookup
            required
            model={model.getModel('statusSolicitado')}
            data={{ idsSelecionados: statusIds }}
            controlStyle={{ marginBottom: '-12px' }}
          />
        </div>
        <div>
          <div style={{ fontWeight: 600 }}>
            {intl.formatMessage({ id: 'fluxoAprovacaoGenerico' })}
          </div>
          <div>{fluxoAprovacao?.nome}</div>
        </div>
        {ehReenvio && (
          <>
            <div>
              <div style={{ fontWeight: 600 }}>
                {intl.formatMessage({ id: 'labelSituacaoSolicitacao' })}
              </div>
              <div>{renderStatus()}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600 }}>
                {intl.formatMessage({ id: 'label.justificativa' })}
              </div>
              <div style={{ minWidth: 0, width: '100%' }}>
                {renderJustificativa(observacaoSolicitacao)}
              </div>
            </div>
          </>
        )}
        <div>
          <Input
            required
            type="textarea"
            maxLength={1000}
            rows={5}
            model={{
              label: intl.formatMessage({ id: 'labelDescricaoJustificativa' }),
              value: model.getModel('justificativa').value,
              requestChange: model.getModel('justificativa').requestChange,
              errors: model.getModel('justificativa').errors,
            }}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ModalFluxoAprovacaoStatus;
